<template>
  <v-container fluid grid-list-md>
    <v-layout row wrap align-center>
      <v-flex xs12 sm6>
        <h2 class="headline">Support</h2>
        <div class="grey--text body-1">
          Check a unit: ONT status, optical level, service and open faults. Nothing here changes the network.
        </div>
      </v-flex>
      <v-flex xs12 sm6 class="text-sm-right">
        <v-btn flat color="primary" :loading="checking" :disabled="!selected" @click="check">
          <v-icon left>refresh</v-icon>Refresh
        </v-btn>
      </v-flex>
    </v-layout>

    <v-layout row wrap>
      <v-flex xs12 sm4>
        <v-select :items="networks" item-text="name" item-value="id"
                  v-model="locationNetworkId" label="Estate"
                  :loading="loadingNetworks" @change="loadUnits"
                  no-data-text="No estates found for your organisation"></v-select>
      </v-flex>
      <v-flex xs12 sm8>
        <v-autocomplete :items="unitItems" v-model="selected"
                        label="Unit" :disabled="!locationNetworkId"
                        :loading="loadingUnits" @change="check"
                        no-data-text="No units on this estate"></v-autocomplete>
      </v-flex>
    </v-layout>

    <div v-if="checking && !result" class="grey--text body-1 my-3">Checking…</div>

    <template v-if="result">
      <v-alert v-for="(w, i) in result.warnings" :key="i" :value="true" type="warning" outline class="mb-2">
        {{ w }}
      </v-alert>
      <v-alert v-if="!result.warnings.length" :value="true" type="success" outline class="mb-2">
        No problems found.
      </v-alert>

      <v-layout row wrap>
        <!-- ONT status -->
        <v-flex xs12 md6>
          <v-card class="support-card">
            <v-card-title class="title">ONT</v-card-title>
            <v-card-text v-if="result.ont">
              <div class="mb-2">
                <v-chip v-if="result.live.available" :color="result.live.online ? 'green' : 'red'" text-color="white" small>
                  {{ result.live.online ? 'Online' : 'Offline' }}
                </v-chip>
                <v-chip v-else color="grey" text-color="white" small>Status unavailable</v-chip>
              </div>
              <div v-if="!result.live.available" class="grey--text mb-2">{{ result.live.reason }}</div>
              <v-layout row wrap>
                <v-flex xs6><strong>Name:</strong> {{ result.ont.name || '-' }}</v-flex>
                <v-flex xs6><strong>PON serial:</strong> {{ result.ont.ponMac || '-' }}</v-flex>
                <v-flex xs6><strong>Serial:</strong> {{ result.ont.serialNumber || '-' }}</v-flex>
                <template v-if="result.live.available">
                  <v-flex xs6><strong>Rx level:</strong> {{ dbm(result.live.rxPower) }}</v-flex>
                  <v-flex xs6><strong>Optic RSSI:</strong> {{ dbm(result.live.opticRssi) }}</v-flex>
                  <v-flex xs6 v-if="!result.live.online"><strong>Status word:</strong> {{ result.live.statusWord || '-' }}</v-flex>
                  <v-flex xs12 class="grey--text caption">
                    Checked {{ formatDate(result.live.checkedAt) }}.
                  </v-flex>
                </template>
              </v-layout>
            </v-card-text>
            <v-card-text v-else class="grey--text">{{ result.noService ? "No ONT is recorded at this unit." : "No ONT is linked to this account." }}</v-card-text>
          </v-card>
        </v-flex>

        <!-- account and package -->
        <v-flex xs12 md6>
          <v-card class="support-card">
            <v-card-title class="title">Account</v-card-title>
            <v-card-text>
              <v-layout row wrap>
                <v-flex xs6><strong>Estate:</strong> {{ result.account.estate || '-' }}</v-flex>
                <v-flex xs6><strong>Unit:</strong> {{ result.account.unitNo || '-' }}</v-flex>
                <template v-if="result.noService">
                  <v-flex xs12><strong>No live service on this unit.</strong></v-flex>
                  <v-flex xs12 v-if="result.lastAccount" class="grey--text">
                    Last account: {{ result.lastAccount.ispName || '-' }}, {{ result.lastAccount.circuitNumber || '-' }},
                    {{ formatDay(result.lastAccount.fnoActivationDate) }} to {{ formatDay(result.lastAccount.fnoDeactivationDate) }}
                  </v-flex>
                  <v-flex xs12 v-else class="grey--text">This unit has never had a service account.</v-flex>
                </template>
                <template v-else>
                <v-flex xs6><strong>Circuit:</strong> {{ result.account.circuitNumber || '-' }}</v-flex>
                <v-flex xs6 v-if="result.side === 'FNO'"><strong>ISP:</strong> {{ result.account.ispName || '-' }}</v-flex>
                <v-flex xs12><strong>Package:</strong> {{ result.account.packageName || '-' }}</v-flex>
                <v-flex xs6><strong>Speed:</strong> {{ speed(result.account) }}</v-flex>
                <v-flex xs6><strong>Activated:</strong> {{ formatDay(result.account.fnoActivationDate) }}</v-flex>
                </template>
              </v-layout>
            </v-card-text>
          </v-card>
        </v-flex>

        <!-- service / bridges -->
        <v-flex xs12 md6>
          <v-card class="support-card">
            <v-card-title class="title">Service</v-card-title>
            <v-card-text>
              <div v-if="!result.bridges.length" class="grey--text">No active bridge on this ONT.</div>
              <v-layout row wrap v-for="b in result.bridges" :key="b.id" align-center>
                <v-flex xs3><strong>VLAN</strong> {{ b.vlan }}</v-flex>
                <v-flex xs3><strong>LAN port</strong> {{ b.lanPort || '-' }}</v-flex>
                <v-flex xs2>
                  <v-chip v-if="!b.linked" small color="orange" text-color="white">Not linked</v-chip>
                </v-flex>
                <v-flex xs4 class="caption grey--text">since {{ formatDate(b.createdAt) }}</v-flex>
              </v-layout>
              <div v-if="result.ports" class="mt-3">
                <div class="caption grey--text">LAN ports (live from the OLT)</div>
                <template v-if="result.ports.available">
                  <v-chip
                    v-for="p in ethPorts"
                    :key="'lan' + p.port"
                    small
                    :color="p.up ? 'green' : 'grey lighten-2'"
                    :text-color="p.up ? 'white' : 'grey darken-2'"
                    :class="{ 'vlan-port': isVlanPort(p.port) }"
                  >LAN {{ p.port }} · {{ p.up ? (p.rate || 'connected') : 'no cable' }}</v-chip>
                  <div class="caption grey--text">
                    <span v-if="ethPorts.some(p => isVlanPort(p.port))">The outlined port carries the ISP's VLAN. </span>Checked {{ formatDate(result.ports.checkedAt) }}.
                  </div>
                </template>
                <div v-else class="caption grey--text">Port status unavailable: {{ result.ports.reason }}</div>
              </div>
            </v-card-text>
          </v-card>
        </v-flex>

        <!-- open faults -->
        <v-flex xs12 md6>
          <v-card class="support-card">
            <v-card-title class="title">Open faults</v-card-title>
            <v-card-text>
              <div v-if="!result.faults.length" class="grey--text">No open faults on this unit.</div>
              <v-layout row wrap v-for="f in result.faults" :key="f.id">
                <v-flex xs4><router-link :to="faultsRoute">{{ f.reference || ('#' + f.id) }}</router-link></v-flex>
                <v-flex xs5>{{ f.title }}</v-flex>
                <v-flex xs3>{{ f.status }}</v-flex>
              </v-layout>
            </v-card-text>
          </v-card>
        </v-flex>
      </v-layout>
    </template>

    <v-snackbar v-model="snack" :timeout="4000">{{ snackText }}</v-snackbar>
  </v-container>
</template>

<script>
import Api from "@/services/Api";

export default {
  name: "Support",
  data() {
    return {
      side: null,
      networks: [],
      units: [],
      locationNetworkId: null,
      // FNO: a location id (any unit on its estates); ISP: one of its own account ids.
      selected: null,
      result: null,
      loadingNetworks: false,
      loadingUnits: false,
      checking: false,
      snack: false,
      snackText: ""
    };
  },
  computed: {
    unitItems() {
      return this.units.map(u => ({
        text:
          (u.unitNo ? "Unit " + u.unitNo : "No unit number") +
          (u.ontName ? "  ·  " + u.ontName : "") +
          (u.circuitNumber ? "  ·  " + u.circuitNumber : "") +
          (this.side === "FNO" && !u.serviceAccountId ? "  ·  no live service" : ""),
        value: this.side === "FNO" ? u.locationId : u.serviceAccountId
      }));
    },
    faultsRoute() {
      return this.side === "FNO" ? "/ispFaultQueue" : "/ispFaults";
    },
    ethPorts() {
      if (!this.result || !this.result.ports || !this.result.ports.available) return [];
      return this.result.ports.ports.filter(p => p.type === "Ethernet");
    }
  },
  mounted() {
    this.loadNetworks();
  },
  methods: {
    loadNetworks() {
      this.loadingNetworks = true;
      Api()
        .get("/ispSupport/networks")
        .then(response => {
          this.side = response.data.side;
          this.networks = response.data.networks;
          this.loadingNetworks = false;
        })
        .catch(() => {
          this.loadingNetworks = false;
          this.notify("Could not load estates.");
        });
    },
    loadUnits() {
      this.units = [];
      this.selected = null;
      this.result = null;
      if (!this.locationNetworkId) return;
      this.loadingUnits = true;
      Api()
        .get("/ispSupport/units/" + this.locationNetworkId)
        .then(response => {
          this.units = response.data;
          this.loadingUnits = false;
        })
        .catch(() => {
          this.loadingUnits = false;
          this.notify("Could not load units.");
        });
    },
    check() {
      if (!this.selected) return;
      const id = this.selected;
      const url = this.side === "FNO" ? "/ispSupport/checkUnit/" : "/ispSupport/check/";
      this.checking = true;
      Api()
        .get(url + id)
        .then(response => {
          // Ignore a slow answer for a unit that is no longer selected.
          if (id !== this.selected) return;
          this.result = response.data;
          this.checking = false;
        })
        .catch(() => {
          this.checking = false;
          this.notify("Could not check this unit.");
        });
    },
    // The network reports optical levels in tenths of a dBm (confirmed against
    // the OLT). 0 means no reading, usually because the ONT is offline.
    dbm(value) {
      if (value === null || value === undefined || value === 0) return "No reading";
      return (value / 10).toFixed(1) + " dBm";
    },
    isVlanPort(port) {
      return !!this.result && this.result.bridges.some(b => b.isActive && b.lanPort === port);
    },
    speed(account) {
      if (!account.downloadMbps && !account.uploadMbps) return "-";
      return (account.downloadMbps || "?") + " / " + (account.uploadMbps || "?") + " Mbps";
    },
    formatDate(value) {
      if (!value) return "";
      return new Date(value).toLocaleString("en-ZA");
    },
    formatDay(value) {
      if (!value) return "-";
      return new Date(value).toLocaleDateString("en-ZA");
    },
    notify(text) {
      this.snackText = text;
      this.snack = true;
    }
  }
};
</script>

<style scoped>
.support-card {
  border-radius: 14px;
  height: 100%;
}
.vlan-port {
  box-shadow: 0 0 0 2px #1976d2;
  font-weight: 600;
}
</style>
